import './docs.scss';
import * as React from 'react';
import { graphql } from 'gatsby';
import classnames from 'classnames';
import BreadcrumbSeparator from 'components/docs/BreadcrumbSeparator';
import * as Core from 'components/core';
import ReactMarkdown from 'components/core/ReactMarkdown';
import Layout from 'components/layout/Standard';
import Navigation from 'components/docs/Navigation';
import MobileNavigation from 'components/docs/MobileNavigation';
import DocsSearch from 'components/docs/Search';
import RelatedDocs from 'components/docs/RelatedDocs';
import SupportBlock from 'components/docs/SupportBlock';
import Tag from 'components/docs/Tag';
import { getLdJson } from 'utils/docs';
import HeadContent from 'components/HeadContent';

const TODOmakeDocsTagsPage = true;
type Props = {
  location: {
    pathname: string;
  };
  path: string;
  data: {
    markdownRemark: {
      html: string;
      headings: Array<{
        value: string;
        depth: number;
      }>;
      frontmatter: {
        tutorialDownloadURL: string | undefined | null;
        youtubeID: string | undefined | null;
        tags: Array<string> | undefined | null;
        title: string;
        product: string;
        topic: string;
        productVersion: number;
        publishedOnForSEO: string;
        updatedOnForSEO?: string;
      };
    };
  };
};
type State = {
  scrolledToTop: boolean;
  currentHeading: string | undefined | null;
};
export default class DocsPage extends React.Component<Props, State> {
  /*
   Scrolling in the navigation is disabled at the top of the page. Otherwise when
   the navigation is scrolled, it's clipped in a way that's confusing. It also
   forces the whole page to scroll when first landing at the page which feels
   more expected.
    Consider extracting this sticky navigation into a separate componenent.
   */

  contentNode: HTMLDivElement | undefined | null;
  updatingFromScroll = false;
  state = {
    scrolledToTop: true,
    currentHeading: null,
  };
  componentDidMount = () => {
    window.addEventListener('scroll', this.handleScroll);
  };
  componentWillUnmount = () => {
    window.removeEventListener('scroll', this.handleScroll);
  };
  handleScroll = () => {
    if (!this.updatingFromScroll) {
      requestAnimationFrame(this.updateFromScroll);
      this.updatingFromScroll = true;
    }
  };
  updateFromScroll = () => {
    this.updatingFromScroll = false;
    const { contentNode } = this;
    if (contentNode == null) {
      return;
    }
    const headings =
      contentNode.querySelectorAll<HTMLHeadingElement>('h1,h2,h3');
    const scrolledToTop = window.pageYOffset === 0;
    const currentHeading = Array.from(headings)
      .reverse()
      .find(
        (heading) =>
          heading.offsetTop < window.scrollY + window.innerHeight / 4.0,
      )?.id;
    if (
      scrolledToTop !== this.state.scrolledToTop ||
      currentHeading !== this.state.currentHeading
    ) {
      this.setState({
        scrolledToTop,
        currentHeading,
      });
    }
  };

  appVersionPathFromPathname = () => {
    return this.props.location.pathname.split('/').splice(0, 3).join('/');
  };

  render() {
    const { scrolledToTop, currentHeading } = this.state;
    const markdown = this.props.data.markdownRemark;
    const { productVersion: version } = markdown.frontmatter;

    const currentAppVersionPath = this.appVersionPathFromPathname();
    const isVersion2 = version === 2;
    const showDocsSearch = !isVersion2;

    const suffix = `QLab ${version} Documentation`;
    let title = markdown.frontmatter.title;
    if (suffix !== title) {
      title = `${markdown.frontmatter.title} | ${suffix}`;
    }
    const productDisplayNames: Record<string, string> = {
      qlab: 'QLab',
      gobutton: 'GoButton',
      qcart: 'QCart',
    };
    const productName =
      productDisplayNames[markdown.frontmatter.product] != null
        ? productDisplayNames[markdown.frontmatter.product]
        : markdown.frontmatter.product;
    const tags = markdown.frontmatter.tags || [];
    const breadcrumbLinks = [
      markdown.frontmatter.productVersion
        ? `${this.props.location.pathname.split('/').slice(0, 3).join('/')}/`
        : '',
      markdown.frontmatter.topic
        ? `${this.props.location.pathname.split('/').slice(0, 4).join('/')}/`
        : '',
      markdown.frontmatter.title
        ? `${this.props.location.pathname.split('/').slice(0, 5).join('/')}/`
        : '',
    ];

    return (
      <Layout>
        <Core.ContentWrapper className="DocsPage">
          <div className="DocsPage-navigation">
            <div className="DocsPage-navigation-stickyContainer">
              <div
                className={classnames('DocsPage-navigation-scrollContainer', {
                  'DocsPage-navigation-scrollContainer--scrollable':
                    !scrolledToTop,
                })}
              >
                <div className="DocsPage-navigation-scrollContainer-inner">
                  <Navigation
                    path={this.props.location.pathname}
                    currentPageHeadings={markdown.headings}
                    currentHeading={currentHeading}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="DocsPage-body">
            {showDocsSearch && (
              <div className="DocsPage-body-search">
                <DocsSearch currentAppVersionPath={currentAppVersionPath} />
              </div>
            )}

            <div className="DocsPage-body-mobileNavigation">
              <MobileNavigation
                path={this.props.location.pathname}
                currentPageHeadings={markdown.headings}
                currentHeading={currentHeading}
              />
            </div>

            <div className="DocsPage-breadcrumbsAndContent">
              <div className="DocsPage-breadcrumbs">
                {markdown.frontmatter.productVersion && (
                  <Core.Link to={breadcrumbLinks[0]}>
                    {productName} {markdown.frontmatter.productVersion}
                  </Core.Link>
                )}
                {markdown.frontmatter.topic && (
                  <>
                    <BreadcrumbSeparator />
                    <Core.Link to={breadcrumbLinks[1]}>
                      {markdown.frontmatter.topic}
                    </Core.Link>
                  </>
                )}
                {markdown.frontmatter.title && (
                  <>
                    <BreadcrumbSeparator />
                    <Core.Link to={breadcrumbLinks[2]}>
                      {markdown.frontmatter.title}
                    </Core.Link>
                  </>
                )}
              </div>

              <div
                className="DocsPage-body-content"
                ref={(element) => (this.contentNode = element)}
              >
                {markdown.frontmatter.tutorialDownloadURL && (
                  <div className="DocsPage-relatedLinks-download">
                    <p className="DocsPage-relatedLinks-download-description">
                      Click here to download the example workspace and related
                      materials for this tutorial.
                    </p>

                    <a
                      href={markdown.frontmatter.tutorialDownloadURL}
                      className="DocsPage-relatedLinks-download-button"
                    >
                      Download
                    </a>
                  </div>
                )}

                <h1
                  className="DocsPage-body-primaryHeading"
                  data-testid="docs-page-heading"
                >
                  {markdown.frontmatter.title}
                </h1>

                {markdown.frontmatter.youtubeID && (
                  <div className="DocsPage-video">
                    <iframe
                      className="DocsPage-video-embed"
                      width="640"
                      height="400"
                      src={`https://www.youtube.com/embed/${markdown.frontmatter.youtubeID}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                <ReactMarkdown prerendered textColor="dark">
                  {markdown.html}
                </ReactMarkdown>
              </div>
            </div>

            {!TODOmakeDocsTagsPage && tags.length > 0 && (
              <div className="DocsPage-tags">
                <span className="DocsPage-tags-title">Tags:</span>
                {tags.map((tag) => (
                  <Tag key={tag} tag={tag} />
                ))}
              </div>
            )}

            <RelatedDocs
              currentPageTags={tags}
              currentPagePath={this.props.path}
              productVersion={markdown.frontmatter.productVersion}
            />

            <div className="DocsPage-body-support">
              <SupportBlock />
            </div>
          </div>
        </Core.ContentWrapper>
      </Layout>
    );
  }
}
export const query = graphql`
  query ($sourcePath: String!) {
    markdownRemark(fileAbsolutePath: { regex: $sourcePath }) {
      html
      headings {
        value
        depth
      }
      frontmatter {
        tutorialDownloadURL
        youtubeID
        tags
        title
        product
        topic
        productVersion
        publishedOnForSEO
        updatedOnForSEO
      }
    }
  }
`;

export const Head = ({ data }: Pick<Props, 'data'>) => {
  const { productVersion, publishedOnForSEO, updatedOnForSEO } =
    data.markdownRemark.frontmatter;
  const suffix = `QLab ${productVersion} Documentation`;

  let title = data.markdownRemark.frontmatter.title;
  if (suffix !== title) {
    title = `${title} | ${suffix}`;
  }

  // Get appropriate ldjson data for the version and docs page
  const ldJson = getLdJson(
    title,
    productVersion,
    publishedOnForSEO,
    updatedOnForSEO,
  );

  return <HeadContent title={title} ldJson={ldJson} />;
};
